import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import SegmentedButtonTsoPq from '@components/Buttons/SegmentedButtonTsoPq';
import ImagePlaceholderTsoPq from '@components/ImagePlaceholder/ImagePlaceholderTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import {
  faFileArrowDown,
  faGripLines,
  faGripLinesVertical,
  faPlus,
  faRotateBackward,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Highcharts } from '@highcharts/react';
import { Box, styled } from '@mui/material';
import { StyledActionsWrapper, StyledHeaderWrapper } from '@pages/Masterdata/StyledGridPageComponents';
import { useAppSelector } from '@redux/hooks';
import {
  clearActiveTableRows,
  clearTableRows,
  setLayout,
  setNewRow,
  upsertTableRow,
} from '@redux/slices/measurementVisualizationSlice';

import { useLazyPostMeasurementGetCsvExportQuery } from '@redux/apiSlices/measurementApi';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import MeasurementVisualizationAppliedParametersTable from './Section/MeasurementVisualizationAppliedParametersTable';
import MeasurementVisualizationChart from './Section/MeasurementVisualizationChart';
import MeasurementVisualizationModal from './Section/MeasurementVisualizationModal';
import MeasurementVisualizationParameters from './Section/MeasurementVisualizationParameters';
import {
  StyledChartSectionTitleActions,
  StyledChartSectionTitleSection,
  StyledChartWrapper,
  StyledManagementMenu,
  StyledManagementMenuHeader,
  StyledMeasurementVisualizationPage,
  StyledVisualisationSection,
} from './styledMeasurementVisualizationPage';
import { csvParams, downloadCsvFile } from './utils';

Highcharts.setOptions({
  lang: {
    resetZoom: 'Zoom zurücksetzen',
    resetZoomTitle: 'Zoom zurücksetzen',
  },
});

const StyledChartContainer = styled(Box, {
  shouldForwardProp: (prop) => !['displayMode'].includes(prop as string),
})(({ displayMode }: { displayMode: string }) => {
  return {
    display: 'grid',
    gridTemplateColumns: displayMode === 'doubleColumn' ? '1fr 1fr' : '1fr',
    gap: '1.5rem',
  };
});

const StyledPageTitle = styled(TypographyTsoPq)(() => {
  return {
    fontWeight: 700,
  };
});

const MeasurementVisualizationPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tableRows = useAppSelector((state) => state.measurementVisualization.tableRows) ?? [];
  const activeTableRows = useAppSelector((state) => state.measurementVisualization.activeTableRows) ?? [];
  const layout = useAppSelector((state) => state.measurementVisualization.layout) ?? 'singleColumn';
  const [triggerCsvExport] = useLazyPostMeasurementGetCsvExportQuery();

  const clearTable = () => {
    dispatch(clearTableRows());
    dispatch(clearActiveTableRows());
  };

  const addNewRow = () => {
    dispatch(
      upsertTableRow({
        uniqueId: `new-${Date.now()}`,
        entityId: 0,
        id: '',
        usedParameters: [],
        measurementDevice: '',
        measurementDeviceTechnicalName: '',
        tsoId: 0,
        aggregationInterval: '',
        aggregationIntervalType: '',
        aggregationIntervalId: 0,
        start: '',
        end: '',
        parameters: [''],
      })
    );
    dispatch(setNewRow(true));
  };

  const handleLayoutChange = (options: string[]) => {
    const selectedOption = options[0] as 'singleColumn' | 'doubleColumn';
    dispatch(setLayout(selectedOption));
  };

  const handleExportAll = () => {
    tableRows.map(async (row) => {
      if (row.measurementDeviceTechnicalName && row.start && row.end && row.usedParameters) {
        try {
          const response = await triggerCsvExport(csvParams(row));
          downloadCsvFile(response.data as string, row);
        } catch (error) {
          console.error(error);
        }
      }
    });
  };

  return (
    <StyledMeasurementVisualizationPage>
      <StyledManagementMenu>
        <StyledManagementMenuHeader>
          <TypographyTsoPq typographyType={'pageSubtitleMDBold'}>
            {t('measurementVisualization.pageTitle')}
          </TypographyTsoPq>
        </StyledManagementMenuHeader>
        <MeasurementVisualizationParameters />
      </StyledManagementMenu>
      <StyledVisualisationSection>
        <StyledHeaderWrapper>
          <StyledPageTitle typographyType={'pageTitle'}>
            {t('measurementVisualization.visualizationSectionTitle')}
          </StyledPageTitle>
          <StyledActionsWrapper>
            <IconButtonTsoPq
              sizeVariant="m"
              buttonType="outlinedPrimary"
              icon={faRotateBackward}
              onClick={clearTable}
              tooltip={t('general.tooltip.resetAll')}
            />
            {tableRows.length > 0 && (
              <IconButtonTsoPq
                sizeVariant="m"
                buttonType="outlinedPrimary"
                icon={faFileArrowDown}
                onClick={() => {
                  handleExportAll();
                }}
                tooltip={t('general.tooltip.exportAllRows')}
              />
            )}
            <IconButtonTsoPq
              sizeVariant="m"
              buttonType="filledPrimary"
              icon={faPlus}
              onClick={addNewRow}
              tooltip={t('general.tooltip.addNewRow')}
            />
          </StyledActionsWrapper>
        </StyledHeaderWrapper>
        <MeasurementVisualizationAppliedParametersTable tableRows={tableRows} />
        <StyledChartWrapper>
          <StyledChartSectionTitleSection>
            <StyledHeaderWrapper>
              <Box>
                <StyledPageTitle typographyType={'pageTitle'}>
                  {t('measurementVisualization.chart.title')}
                </StyledPageTitle>
                <TypographyTsoPq typographyType={'pageSubtitleSM'}>
                  {t('measurementVisualization.chart.subtitle')}
                </TypographyTsoPq>
              </Box>
            </StyledHeaderWrapper>
            <StyledChartSectionTitleActions>
              {activeTableRows.length > 0 && (
                <SegmentedButtonTsoPq
                  options={[
                    {
                      segmentValue: t('singleColumn'),
                      segmentLabel: <FontAwesomeIcon icon={faGripLines} />,
                    },
                    {
                      segmentValue: t('doubleColumn'),
                      segmentLabel: <FontAwesomeIcon icon={faGripLinesVertical} />,
                    },
                  ]}
                  defaultValue={'singleColumn'}
                  handleChange={handleLayoutChange}
                  sizeVariant="s"
                />
              )}
            </StyledChartSectionTitleActions>
          </StyledChartSectionTitleSection>
          <StyledChartContainer displayMode={layout}>
            {activeTableRows.length > 0 ? (
              activeTableRows.map((chart: any) => {
                return <MeasurementVisualizationChart key={chart.uniqueId} {...chart} />;
              })
            ) : (
              <ImagePlaceholderTsoPq
                title={t('measurementVisualization.chart.placeholder.title')}
                description={t('measurementVisualization.chart.placeholder.descri')}
              />
            )}
          </StyledChartContainer>
        </StyledChartWrapper>
      </StyledVisualisationSection>
      {<MeasurementVisualizationModal />}
    </StyledMeasurementVisualizationPage>
  );
};

export default MeasurementVisualizationPage;
